import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { postProjectTemplateTdm } from '@/api/Sherpa/ProjectTemplate';
import type { PostProjectTemplateTdmPayload } from '@/api/Sherpa/ProjectTemplate/types';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useProjectTemplateFileUpload } from '@/hooks/useProjectTemplateFileUpload';
import { notifyUser } from '@/store/Toast';
import type { TitleInterface } from '@/types/Account';
import { getAcceptedFileTypes, sanitizeResponseError } from '@/utils';

interface INewMilestoneFormProps {
  handleModal: () => void;
  phase: string;
}

interface ICreateTemplateTaskPayload {
  data: PostProjectTemplateTdmPayload;
  templateId: number;
}

const initialData = {
  milestone: '',
  leadTitle: 0,
  supportTitle: 0,
  phase: '',
};

const NewMilestoneForm = ({ phase, handleModal }: INewMilestoneFormProps) => {
  const pageParams = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { watch } = useFormContext();
  const selectedLead = watch('lead');
  const selectedSupport = watch('support');

  const templateFileRef = useRef<File[]>(null);

  const fetchOrgTitles = useOrgTitles({});
  const titles = fetchOrgTitles?.data?.data;

  const signAndUploadFiles = useProjectTemplateFileUpload('MILESTONE');

  const createTemplateMilestone = useMutation((payload: ICreateTemplateTaskPayload) => postProjectTemplateTdm(payload), {
    onSuccess: (response: any) => {
      if (templateFileRef.current) {
        signAndUploadFiles.uploadTemplate({
          templateFile: templateFileRef.current,
          tdmId: response.data.id,
          templateId: Number(pageParams.templateId),
        });
        return;
      }
      handleAddSuccess();
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const handleAddSuccess = useCallback(() => {
    queryClient.invalidateQueries(['get-project-template-tdm']);
    handleModal();
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'Project Template task added successfully',
        toastType: 'success',
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, dispatch, handleModal]);

  useEffect(() => {
    if (signAndUploadFiles.uploadSuccess) {
      handleAddSuccess();
    }
  }, [signAndUploadFiles.uploadSuccess, handleAddSuccess]);

  const addTemplateMilestone = (data: any) => {
    const { milestone, lead, support, note, template } = data;

    const transformedData: PostProjectTemplateTdmPayload = {
      resourceType: 'MILESTONE',
      templateData: {
        milestone,
        phase: phase.toUpperCase(),
        leadTitleId: lead.id,
        supportTitleId: support?.id,
        note,
      },
    };

    if (template?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      data.template?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }

      if (validFiles.length > 0) {
        templateFileRef.current = validFiles;
      }
    }

    createTemplateMilestone.mutate({
      data: transformedData,
      templateId: Number(pageParams.templateId),
    });
  };

  return (
    <HookForm noDefaultClass className='w-full' onSubmit={addTemplateMilestone}>
      <h2 className='m-0 mb-5'>New Milestone</h2>
      <div className='space-y-5'>
        <HookInput type='text' label='Milestone Name' name='milestone' id='name' placeholder='e.g. Sage Office' required />

        <HookReactSingleSelect
          id='leadOrgTitleId'
          options={titles?.filter((data: any) => data?.id !== selectedSupport?.id)}
          getOptionLabel={(option) => (option as TitleInterface).name}
          getOptionValue={(option) => (option as TitleInterface).id.toString()}
          placeholder='Select Lead'
          name='lead'
          label='Lead Organizational Title'
          required
        />
        <HookReactSingleSelect
          id='supportOrgTitleId'
          options={titles?.filter((data: any) => data?.id !== selectedLead?.id)}
          getOptionLabel={(option) => (option as TitleInterface).name}
          getOptionValue={(option) => (option as TitleInterface).id.toString()}
          placeholder='Select support'
          name='support'
          label='Support Organizational Title'
        />
        <HookTextarea name='note' label='Notes' placeholder='Type your notes here...' />
        <HookFileInput label='Template' name='template' id='template' multiple />
        <div className='flex justify-end gap-4'>
          <Button label='Cancel' btnStyle='btn-neutral' type='button' disabled={createTemplateMilestone.isLoading} onClick={handleModal} />
          <Button
            label={`${createTemplateMilestone.isLoading ? 'Creating...' : 'Add'}`}
            disabled={createTemplateMilestone.isLoading}
            btnStyle='btn-primary'
            type='submit'
          />
        </div>
      </div>
    </HookForm>
  );
};

const NewMilestoneFormProvider = ({ phase, handleModal }: INewMilestoneFormProps) => {
  return (
    <Modal>
      <HookFormProvider defaultValues={initialData}>
        <NewMilestoneForm phase={phase} handleModal={handleModal} />
      </HookFormProvider>
    </Modal>
  );
};

export default NewMilestoneFormProvider;
